import { ColDef } from "ag-grid-enterprise";
import {
  DATE_FORMAT_MMDDYYYY_HHMM_A,
  DateFilter,
  DateUtils
} from "utils/dateutils";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import CustomDateSetFilter from "entity-app/custom-filters/custom-date-filter";
import dayjs, { Dayjs } from "utils/dayjs-config";

export type TActivityLogRow = {
  id: string;
  created_at: string;
  category: string;
  module: string;
  event_type: string;
  user: string;
  email?: string;
  file_name: string;
  project_name: string;
};

/** Audit timestamps are UTC; format them as UTC so the grid does not shift to the project zone. */
export const formatActivityLogUtcDate = (value?: string | null): string =>
  DateUtils.formatUtc(value, DATE_FORMAT_MMDDYYYY_HHMM_A);

export const formatActivityLogUserExportValue = (
  data?: Pick<TActivityLogRow, "user" | "email"> | null
): string => {
  const name = data?.user?.trim() ?? "";
  const email = data?.email?.trim();
  if (name && email) return `${name}\n${email}`;
  return name || email || "";
};

/**
 * Backward-looking presets for the shared date filter. The log records what
 * already happened, so the due-date defaults would never match.
 */
export const ACTIVITY_LOG_DATE_PRESETS = [
  "Today",
  "Past 7 days",
  "This month",
  "Last 30 days",
  "This quarter",
  "Current year"
];

/** Days covered when the user has applied no date filter of their own. */
export const ACTIVITY_LOG_DEFAULT_RANGE_DAYS = 30;

/** Widest Custom range the log will query. */
export const ACTIVITY_LOG_MAX_RANGE_DAYS = 365;

/** Seeded on landing so the filter pin shows what the grid is actually showing. */
export const ACTIVITY_LOG_DEFAULT_DATE_FILTER = {
  filterType: "set",
  values: ["Last 30 days"]
};

/** AG Grid's canonical date-model format, which get_audit_logs parses. */
const API_DATE_FORMAT = "YYYY-MM-DD HH:mm:ss";

export type TActivityLogDateFilterModel = {
  filterType?: string;
  type?: string | null;
  values?: string[] | null;
  dateFrom?: string | Date | null;
  dateTo?: string | Date | null;
};

/** The only shape get_audit_logs understands: an explicit datetime range. */
export type TActivityLogApiDateFilter = {
  filterType: "date";
  type: "inRange";
  dateFrom: string;
  dateTo: string;
};

const parseFilterDateInput = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const parsed = DateUtils.dateTimeObj(value);
  return parsed.isValid() ? parsed.toDate() : null;
};

/**
 * Window covered by one preset key, mirroring the shared filter's handler so
 * the rows the server returns are a superset of what the grid then shows.
 */
const presetWindow = (key: string, now: Dayjs): [Dayjs, Dayjs] | null => {
  const today = now.startOf("day");
  switch (key) {
    case "Today":
      return [today, today.endOf("day")];
    case "Past 7 days":
      return [today.subtract(6, "day"), today.endOf("day")];
    case "Last 30 days":
      return [today.subtract(29, "day"), today.endOf("day")];
    case "This month":
      return [today.startOf("month"), today.endOf("month")];
    case "This quarter": {
      // No quarterOfYear plugin is registered, so derive the first month.
      const start = today
        .month(today.month() - (today.month() % 3))
        .startOf("month");
      return [start, start.add(2, "month").endOf("month")];
    }
    case "Current year":
      return [today.startOf("year"), today.endOf("year")];
    default:
      return null;
  }
};

const toApiDateFilter = (
  from: Dayjs,
  to: Dayjs
): TActivityLogApiDateFilter => ({
  filterType: "date",
  type: "inRange",
  dateFrom: from.format(API_DATE_FORMAT),
  dateTo: to.format(API_DATE_FORMAT)
});

/**
 * get_audit_logs accepts only an explicit datetime range, so every preset the
 * filter UI can produce is resolved here rather than shipped as its own key.
 * Several presets can be ticked at once; their windows are unioned.
 */
export const resolveActivityLogApiDateFilter = (
  filter?: TActivityLogDateFilterModel | null,
  now: Dayjs = dayjs()
): TActivityLogApiDateFilter => {
  const today = now.startOf("day");
  const fallback = (): TActivityLogApiDateFilter =>
    toApiDateFilter(
      today.subtract(ACTIVITY_LOG_DEFAULT_RANGE_DAYS - 1, "day"),
      today.endOf("day")
    );

  const values = Array.isArray(filter?.values) ? filter.values : [];

  if (values.includes("CustomDateRange")) {
    const from = parseFilterDateInput(filter?.dateFrom);
    const to = parseFilterDateInput(filter?.dateTo);
    if (!from || !to || from.getTime() > to.getTime()) return fallback();
    return toApiDateFilter(dayjs(from).startOf("day"), dayjs(to).endOf("day"));
  }

  const windows = values
    .map((value) => presetWindow(value, now))
    .filter((window): window is [Dayjs, Dayjs] => window !== null);
  if (!windows.length) return fallback();

  const from = windows.reduce(
    (earliest, window) => (earliest[0].isBefore(window[0]) ? earliest : window),
    windows[0]
  )[0];
  const to = windows.reduce(
    (latest, window) => (latest[1].isAfter(window[1]) ? latest : window),
    windows[0]
  )[1];
  return toApiDateFilter(from, to);
};

/** True when a Custom range is complete, ordered and inside the 1-year ceiling. */
export const isActivityLogCustomDateFilterValid = (
  filter?: TActivityLogDateFilterModel | null
): boolean => {
  if (!filter || (filter.type !== "inRange" && filter.type !== "customRange")) {
    return true;
  }
  const from = parseFilterDateInput(filter.dateFrom);
  const to = parseFilterDateInput(filter.dateTo);
  if (!from || !to || from.getTime() > to.getTime()) return false;
  return (
    dayjs(to).startOf("day").diff(dayjs(from).startOf("day"), "day") <=
    ACTIVITY_LOG_MAX_RANGE_DAYS
  );
};

export const getActivityLogDefaultColDef = (): ColDef => ({
  sortable: true,
  resizable: true,
  filter: "agSetColumnFilter",
  sortingOrder: ["asc", "desc"],
  comparator: comparatorWithEmptyAtBottom,
  minWidth: 120
});

export const getActivityLogColumnDefs = ({
  showProjectName = false
}: {
  showProjectName?: boolean;
} = {}): ColDef<TActivityLogRow>[] => [
  {
    colId: "date_time",
    headerName: "Date and Time (in UTC)",
    field: "created_at",
    sort: "desc",
    sortingOrder: ["desc", "asc"],
    filter: CustomDateSetFilter,
    filterParams: {
      presets: ACTIVITY_LOG_DATE_PRESETS,
      maxRangeDays: ACTIVITY_LOG_MAX_RANGE_DAYS,
      // Last 30 days is seeded on landing, so Custom must be able to replace it.
      allowCustomWithPresets: true,
      // Every audit row carries a timestamp, so "(Blanks)" can never match.
      includeBlanks: false
    },
    menuTabs: ["filterMenuTab"],
    minWidth: 200,
    valueGetter: ({ data }) => data?.created_at || null,
    valueFormatter: ({ value }) => formatActivityLogUtcDate(value),
    comparator: (valueA, valueB, nodeA, nodeB, isInverted) =>
      DateFilter.dateTimeComparator(
        nodeA?.data?.created_at || valueA,
        nodeB?.data?.created_at || valueB,
        nodeA,
        nodeB,
        isInverted
      ),
    tooltipValueGetter: ({ data }) => formatActivityLogUtcDate(data?.created_at)
  },
  ...(showProjectName
    ? [
        {
          colId: "project_name",
          headerName: "Project Name",
          field: "project_name",
          filter: "agSetColumnFilter",
          menuTabs: ["filterMenuTab"],
          minWidth: 180,
          tooltipField: "project_name"
        } as ColDef<TActivityLogRow>
      ]
    : []),
  // {
  //   colId: "category",
  //   headerName: "Category",
  //   field: "category",
  //   filter: "agSetColumnFilter",
  //   menuTabs: ["filterMenuTab"],
  //   minWidth: 160,
  //   tooltipField: "category"
  // },
  {
    colId: "user",
    headerName: "User",
    field: "user",
    menuTabs: ["filterMenuTab"],
    minWidth: 180,
    cellRenderer: ({ data }: { data?: TActivityLogRow }) => (
      <div className="flex min-w-0 flex-col justify-center leading-tight py-0.5">
        <span className="truncate">{data?.user ?? ""}</span>
        {data?.email ? (
          <span className="truncate text-xs text-black/45">{data.email}</span>
        ) : null}
      </div>
    ),
    tooltipValueGetter: ({ data }) => {
      const name = data?.user?.trim();
      const email = data?.email?.trim();
      if (name && email) return `${name}\n${email}`;
      return name || email || "";
    },
    getQuickFilterText: ({ data }) =>
      [data?.user, data?.email].filter(Boolean).join(" ")
  },
  {
    colId: "event_type",
    headerName: "Event Type",
    field: "event_type",
    menuTabs: ["filterMenuTab"],
    minWidth: 180,
    tooltipField: "event_type"
  },
  {
    colId: "module",
    headerName: "Module",
    field: "module",
    menuTabs: ["filterMenuTab"],
    minWidth: 130
  },
  {
    colId: "file_name",
    headerName: "File Name",
    field: "file_name",
    menuTabs: ["filterMenuTab"],
    flex: 1,
    minWidth: 200,
    tooltipField: "file_name"
  }
];
